// Typed-token document AST for project briefs.
//
// Each sentence is parsed into a small set of typed entities. The
// downstream mapper queries this structured tree — never re-scans the
// raw string — so confidence numbers are reproducible and the
// provenance for every IngestField is just an (sentence_index, span).
//
// This replaces the per-field regex extractor with one parse pass +
// a typed query layer, which is the same shape the Python backend will
// produce when gliner-large / fastcoref / mineru / ifcopenshell run
// against the input. The TS version here covers free-text briefs;
// CAD and PDF land via their own adapters that emit the same AST.

export type EntityKind =
  | "Money"           // currency + magnitude
  | "Quantity"        // number + unit (sqft, m², lux, etc)
  | "Count"           // bare integer with a noun (3 BHK, 2 bedrooms)
  | "Address"         // a city/locality token
  | "Date"            // month/year, ISO date, "October 2026"
  | "ProperNoun"      // capitalised name (likely Person)
  | "PropertyKind"    // residential / commercial / retail / hospitality
  | "Style"           // modern / scandi / industrial / japandi / indian-traditional
  | "Tier"            // standard / premium / luxury
  | "OccupantClass";  // couple / kids / elders / pets / home-office

export interface Span { start: number; end: number }

export interface Entity {
  kind: EntityKind;
  value: string;             // canonical value
  raw: string;               // original text
  unit?: string;              // for Quantity / Money
  magnitude?: number;         // for Money / Count
  span: Span;
  confidence: number;
}

export interface SentenceAST {
  index: number;
  text: string;
  span: Span;
  entities: Entity[];
}

export interface DocAST {
  text: string;
  sentences: SentenceAST[];
}

// ── classifier tables ────────────────────────────────────────────────
const PROPERTY_KIND_MAP: Record<string, "residential" | "commercial" | "retail" | "hospitality"> = {
  apartment: "residential",
  flat: "residential",
  villa: "residential",
  home: "residential",
  house: "residential",
  bungalow: "residential",
  duplex: "residential",
  residential: "residential",
  office: "commercial",
  commercial: "commercial",
  workspace: "commercial",
  retail: "retail",
  store: "retail",
  shop: "retail",
  showroom: "retail",
  restaurant: "hospitality",
  hotel: "hospitality",
  cafe: "hospitality",
  hospitality: "hospitality",
};

const STYLES = ["modern", "scandi", "scandinavian", "japandi", "industrial", "minimal", "minimalist", "indian-traditional", "traditional", "boho", "art-deco"] as const;
const TIERS = ["standard", "premium", "luxury"] as const;
const OCCUPANT_CUES: Array<{ cls: "couple" | "kids" | "elders" | "pets" | "home-office"; cues: readonly string[] }> = [
  { cls: "couple",      cues: ["couple", "husband", "wife", "partner"] },
  { cls: "kids",        cues: ["kid", "kids", "child", "children", "toddler", "baby"] },
  { cls: "elders",      cues: ["elder", "elderly", "parents", "in-laws", "grand"] },
  { cls: "pets",        cues: ["pet", "pets", "dog", "labrador", "cat", "puppy"] },
  { cls: "home-office", cues: ["home office", "home-office", "wfh", "work from home", "desk"] },
];

// Cities we can resolve with no API call. The Python adapter swaps in
// gliner's GPE label or a geocoder for general coverage; the cockpit
// preview ships a curated India-first list because that's our pilot.
const KNOWN_CITIES = new Set([
  "bengaluru", "bangalore", "mumbai", "delhi", "new delhi", "hyderabad",
  "chennai", "kolkata", "pune", "jaipur", "ahmedabad", "kochi", "goa",
  "gurgaon", "noida", "chandigarh", "lucknow", "indore",
]);

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"] as const;
function normaliseMonth(m: string): string {
  return m.toLowerCase().slice(0, 3);
}

// ── per-sentence parsers (each returns 0..N entities) ────────────────
function parseMoney(text: string, offset: number): Entity[] {
  const out: Entity[] = [];
  const re = /₹\s?(\d+(?:\.\d+)?)\s*(cr|crore|crores|lakh|lakhs|k)?\b/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const n = parseFloat(m[1] ?? "0");
    const unitTok = (m[2] ?? "").toLowerCase();
    const factor = unitTok.startsWith("cr") ? 1e7 : unitTok.startsWith("lakh") ? 1e5 : unitTok === "k" ? 1e3 : 1;
    out.push({
      kind: "Money",
      value: `INR ${Math.round(n * factor)}`,
      raw: m[0],
      unit: "INR",
      magnitude: n * factor,
      span: { start: offset + m.index, end: offset + m.index + m[0].length },
      confidence: 0.95,
    });
  }
  return out;
}

function parseQuantity(text: string, offset: number): Entity[] {
  const out: Entity[] = [];
  // sqft with optional comma in number
  const reSqft = /(\d{1,3}(?:,\d{3})+|\d{3,6})\s*(sq\s*ft|sqft|square\s*feet|m²|sqm)/gi;
  let m: RegExpExecArray | null;
  while ((m = reSqft.exec(text)) !== null) {
    const raw = m[1] ?? "";
    const num = parseInt(raw.replace(/,/g, ""), 10);
    out.push({
      kind: "Quantity",
      value: String(num),
      raw: m[0],
      unit: (m[2] ?? "sqft").replace(/\s+/g, "").toLowerCase(),
      magnitude: num,
      span: { start: offset + m.index, end: offset + m.index + m[0].length },
      confidence: 0.95,
    });
  }
  return out;
}

function parseCount(text: string, offset: number): Entity[] {
  const out: Entity[] = [];
  const re = /(\d+)\s*(BHK|bedroom|bedrooms|br|bath|baths|bathroom)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    out.push({
      kind: "Count",
      value: m[1] ?? "",
      raw: m[0],
      unit: (m[2] ?? "").toLowerCase(),
      magnitude: parseInt(m[1] ?? "0", 10),
      span: { start: offset + m.index, end: offset + m.index + m[0].length },
      confidence: 0.93,
    });
  }
  return out;
}

function parseAddress(text: string, offset: number): Entity[] {
  const out: Entity[] = [];
  const re = /\b(?:in|at)\s+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)\b/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const candidate = (m[1] ?? "").toLowerCase();
    const confidence = KNOWN_CITIES.has(candidate) ? 0.9 : 0.55;
    out.push({
      kind: "Address",
      value: m[1] ?? "",
      raw: m[0],
      span: { start: offset + m.index, end: offset + m.index + m[0].length },
      confidence,
    });
  }
  return out;
}

function parseDate(text: string, offset: number): Entity[] {
  const out: Entity[] = [];
  const reMonth = new RegExp(`\\b(${MONTHS.join("|")})[a-z]*\\s+(\\d{4})\\b`, "gi");
  let m: RegExpExecArray | null;
  while ((m = reMonth.exec(text)) !== null) {
    const mm = normaliseMonth(m[1] ?? "");
    out.push({
      kind: "Date",
      value: `${m[2]}-${String(MONTHS.indexOf(mm as (typeof MONTHS)[number]) + 1).padStart(2, "0")}`,
      raw: m[0],
      span: { start: offset + m.index, end: offset + m.index + m[0].length },
      confidence: 0.75,
    });
  }
  const reIso = /\b(20\d{2}-\d{2}-\d{2})\b/g;
  while ((m = reIso.exec(text)) !== null) {
    out.push({
      kind: "Date",
      value: m[1] ?? "",
      raw: m[1] ?? "",
      span: { start: offset + m.index, end: offset + m.index + m[0].length },
      confidence: 0.95,
    });
  }
  return out;
}

function parseProperNouns(text: string, offset: number): Entity[] {
  // Names of people — we only care that they exist; coref resolves the rest.
  const out: Entity[] = [];
  const re = /\b([A-Z][a-z]{2,})\b/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const w = m[1] ?? "";
    // Filter out obvious non-person caps (month names, cities, generic words).
    if (KNOWN_CITIES.has(w.toLowerCase())) continue;
    if (MONTHS.includes(normaliseMonth(w) as (typeof MONTHS)[number])) continue;
    if (["Budget", "Move", "October", "Mumbai"].includes(w)) continue;
    out.push({
      kind: "ProperNoun",
      value: w,
      raw: w,
      span: { start: offset + m.index, end: offset + m.index + w.length },
      confidence: 0.6,
    });
  }
  return out;
}

function parsePropertyKind(text: string, offset: number): Entity[] {
  const out: Entity[] = [];
  const lower = text.toLowerCase();
  for (const [token, cls] of Object.entries(PROPERTY_KIND_MAP)) {
    const idx = lower.indexOf(token);
    if (idx === -1) continue;
    out.push({
      kind: "PropertyKind",
      value: cls,
      raw: token,
      span: { start: offset + idx, end: offset + idx + token.length },
      confidence: 0.9,
    });
  }
  return out;
}

function parseStyle(text: string, offset: number): Entity[] {
  const out: Entity[] = [];
  const lower = text.toLowerCase();
  for (const s of STYLES) {
    const idx = lower.indexOf(s);
    if (idx === -1) continue;
    out.push({
      kind: "Style",
      value: s === "minimal" || s === "minimalist" ? "scandi" : s === "traditional" ? "indian-traditional" : s === "scandinavian" ? "scandi" : s,
      raw: s,
      span: { start: offset + idx, end: offset + idx + s.length },
      confidence: 0.85,
    });
  }
  return out;
}

function parseTier(text: string, offset: number): Entity[] {
  const out: Entity[] = [];
  const lower = text.toLowerCase();
  for (const t of TIERS) {
    const idx = lower.indexOf(t);
    if (idx === -1) continue;
    out.push({
      kind: "Tier", value: t, raw: t,
      span: { start: offset + idx, end: offset + idx + t.length },
      confidence: 0.9,
    });
  }
  return out;
}

function parseOccupants(text: string, offset: number): Entity[] {
  const out: Entity[] = [];
  const lower = text.toLowerCase();
  for (const { cls, cues } of OCCUPANT_CUES) {
    for (const c of cues) {
      const idx = lower.indexOf(c);
      if (idx === -1) continue;
      out.push({
        kind: "OccupantClass", value: cls, raw: c,
        span: { start: offset + idx, end: offset + idx + c.length },
        confidence: 0.78,
      });
      break;
    }
  }
  return out;
}

// ── top-level parse ──────────────────────────────────────────────────
export function parseBriefAST(text: string): DocAST {
  const sentences: SentenceAST[] = [];
  // Split by . ; \n while preserving offsets.
  const splitRe = /[.;\n]+/g;
  let cursor = 0;
  let m: RegExpExecArray | null;
  let idx = 0;
  while ((m = splitRe.exec(text)) !== null) {
    const segment = text.slice(cursor, m.index);
    if (segment.trim().length > 0) {
      sentences.push(buildSentence(segment, cursor, idx++));
    }
    cursor = m.index + m[0].length;
  }
  const tail = text.slice(cursor);
  if (tail.trim().length > 0) sentences.push(buildSentence(tail, cursor, idx++));

  return { text, sentences };
}

function buildSentence(segment: string, offset: number, index: number): SentenceAST {
  const entities: Entity[] = [
    ...parseMoney(segment, offset),
    ...parseQuantity(segment, offset),
    ...parseCount(segment, offset),
    ...parseAddress(segment, offset),
    ...parseDate(segment, offset),
    ...parsePropertyKind(segment, offset),
    ...parseStyle(segment, offset),
    ...parseTier(segment, offset),
    ...parseOccupants(segment, offset),
    ...parseProperNouns(segment, offset),
  ];
  return {
    index,
    text: segment,
    span: { start: offset, end: offset + segment.length },
    entities,
  };
}

// ── typed queries over the AST ───────────────────────────────────────
export function entitiesOfKind(ast: DocAST, kind: EntityKind): Entity[] {
  const out: Entity[] = [];
  for (const s of ast.sentences) for (const e of s.entities) if (e.kind === kind) out.push(e);
  return out;
}
